import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  LayoutDashboard,
  Users,
  FolderKanban,
  Clock,
  FileText,
  Wallet,
  TrendingUp,
  FileBarChart,
  LogOut,
  User,
  Menu,
  X,
  Settings,
  Building2,
  Layers,
  Repeat,
  BookOpen,
  Receipt,
  Sparkles
} from "lucide-react";
import { useState } from "react";

const navItems = [
  { path: "/", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/clients", icon: Users, label: "Clients" },
  { path: "/projects", icon: FolderKanban, label: "Projects" },
  { path: "/timesheets", icon: Clock, label: "Timesheets" },
  { path: "/invoices", icon: FileText, label: "Invoices" },
  { path: "/recurring-invoices", icon: Repeat, label: "Recurring" },
  { path: "/expenses", icon: Wallet, label: "Expenses" },
  { path: "/bills", icon: Receipt, label: "Bills" },
  { path: "/chart-of-accounts", icon: BookOpen, label: "Chart of Accounts" },
  { path: "/forecasts", icon: TrendingUp, label: "Forecasts" },
  { path: "/reports", icon: FileBarChart, label: "Reports" },
  { path: "/finance-advisor", icon: Sparkles, label: "Finance Advisor" },
  { path: "/bank-import", icon: Building2, label: "Bank Import" },
  { path: "/life-finance", icon: Layers, label: "Life Finance" },
  { path: "/settings", icon: Settings, label: "Settings" },
];

export const DashboardLayout = ({ children, title }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background flex" data-testid="dashboard-layout">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <aside 
        className={`
          fixed lg:static inset-y-0 left-0 z-50
          w-64 bg-card border-r border-border
          transform transition-transform duration-200
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-6 border-b border-border">
            <h1 
              className="text-xl font-bold text-primary"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              FinanceHub
            </h1>
            <button 
              className="lg:hidden p-1 hover:bg-accent rounded"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="px-3 space-y-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === "/"}
                  className={({ isActive }) =>
                    `sidebar-link ${isActive ? 'active' : 'text-muted-foreground hover:text-foreground'}`
                  }
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </nav>
          </ScrollArea>
          
          {/* User section */}
          <div className="p-4 border-t border-border">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-accent transition-colors">
                  <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium truncate">{user?.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={handleLogout}
                  className="text-destructive focus:text-destructive cursor-pointer"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>
      
      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top bar */}
        <header className="h-16 border-b border-border bg-card/80 backdrop-blur-sm sticky top-0 z-30 flex items-center px-4 lg:px-8">
          <button 
            className="lg:hidden p-2 hover:bg-accent rounded-lg mr-4"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </button>
          <h2 className="page-title text-xl lg:text-2xl">{title}</h2>
        </header>
        
        {/* Page content */}
        <main className="flex-1 p-4 lg:p-8 animate-fade-in">
          {children}
        </main>
      </div>
    </div>
  );
};
